// -----------------------------------------------------------------------------
// Gridlines (v0.7.0) — pure logic, unit-tested in tests/unit/grid.test.ts. No React, no pdf.js here.
//
//   geometryFromOps()  pdf.js operator list → straight stroked segments + small closed curves (grid bubbles), in
//                      page units as drawn (pdf.js viewport at scale 1, rotation applied). Text items are added by
//                      core/pdfPlan.ts (getTextContent).
//   detectGrid()       A6.4 "Detection": every bubble (circle, or a short grid-like label) sends out the longest
//                      dashed / broken straight run that points at its centre. Gaps up to joinGap (pt) are joined;
//                      runs shorter than minLength × the sheet's shorter side are skipped. Bubble without readable text
//                      → label null (amber "label unreadable"). Same label on two different lines → both flagged check.
//   pinReading()       A5.2 / A6.4 cursor: "C/17" on a line, "D/12–13" between two, "TB · C/4–5" inside a zone.
//   gridCounts()       labelled / to check (A6.4 detection card; same rule as issues_grid_save in 0016).
//
// Coordinates stored in the database (GridLine, GridZone) are 0–1 across / down the plan page as drawn — the same
// space as issue pins (pin_x / pin_y) — so a pin and the grid always agree. Angles and distances use the page's real
// shape: ratio = page height / width (stored with the grid).
// Used by: core/pdfPlan.ts (pageGeometry), components/GridSetup.tsx, components/Plan.tsx, pages/IssuePage.tsx.
// -----------------------------------------------------------------------------
import type { GridLine, GridZone } from "../../core/api/types";

export interface Seg { x1: number; y1: number; x2: number; y2: number }
/** A text run as drawn: (x, y) = start of the baseline; w = advance width; h = font height (page units). */
export interface TextItem { s: string; x: number; y: number; w: number; h: number; rot: number }
export interface Circle { cx: number; cy: number; r: number }
export interface PageGeometry { width: number; height: number; segs: Seg[]; circles: Circle[]; texts: TextItem[] }
export interface DetectOptions { /** share of the sheet's shorter side (A6.4 "Min length", default 0.2) */ minLength: number; /** pt (A6.4 "Join gaps", default 12) */ joinGap: number }
export const DETECT_DEFAULTS: DetectOptions = { minLength: 0.2, joinGap: 12 };

// ---- 1. pdf.js operator list → geometry ------------------------------------------------------------------------------
type M = [number, number, number, number, number, number];
const mul = (a: M, b: M): M => [a[0] * b[0] + a[2] * b[1], a[1] * b[0] + a[3] * b[1], a[0] * b[2] + a[2] * b[3], a[1] * b[2] + a[3] * b[3],
  a[0] * b[4] + a[2] * b[5] + a[4], a[1] * b[4] + a[3] * b[5] + a[5]];

/** The pdf.js OPS numbers this needs (pass pdfjs.OPS). */
export interface OpsTable { save: number; restore: number; transform: number; constructPath: number; stroke: number; closeStroke: number;
  fill: number; eoFill: number; fillStroke: number; eoFillStroke: number; closeFillStroke: number; closeEOFillStroke: number; endPath: number;
  moveTo: number; lineTo: number; curveTo: number; curveTo2: number; curveTo3: number; closePath: number; rectangle: number;
  paintFormXObjectBegin: number; paintFormXObjectEnd: number }

/**
 * Walk the operator list. `base` = viewport transform (page → drawn page, scale 1). Straight pieces of STROKED paths
 * become segments; small closed curved sub-paths (stroked or filled) whose box is about square become circles.
 * Long paths of the same stroke are kept piece by piece (a dashed gridline is many short pieces).
 */
export function geometryFromOps(fnArray: ArrayLike<number>, argsArray: ArrayLike<unknown>, O: OpsTable, base: number[], maxSegs = 600_000): { segs: Seg[]; circles: Circle[] } {
  let ctm = base.slice(0, 6) as M; const stack: M[] = [];
  const segs: Seg[] = [], circles: Circle[] = [];
  let pend: Seg[] = [], groups: Seg[][] = [], curves: { minX: number; minY: number; maxX: number; maxY: number; n: number }[] = [];
  let cx = 0, cy = 0, sx = 0, sy = 0, sub: { minX: number; minY: number; maxX: number; maxY: number; n: number } | null = null;
  const T = (x: number, y: number) => [ctm[0] * x + ctm[2] * y + ctm[4], ctm[1] * x + ctm[3] * y + ctm[5]];
  const grow = (x: number, y: number) => { if (!sub) return; const [X, Y] = T(x, y); sub.minX = Math.min(sub.minX, X); sub.maxX = Math.max(sub.maxX, X); sub.minY = Math.min(sub.minY, Y); sub.maxY = Math.max(sub.maxY, Y); };
  const bump = () => { if (sub) sub.n++; };
  const startSub = (x: number, y: number) => { if (sub && sub.n >= 2) curves.push(sub); const [X, Y] = T(x, y); sub = { minX: X, maxX: X, minY: Y, maxY: Y, n: 0 }; if (pend.length) groups.push(pend); pend = []; };
  const flush = (stroked: boolean) => {
    if (pend.length) groups.push(pend);
    if (stroked && segs.length < maxSegs) for (const g of groups) for (const s of g) segs.push(s);
    // Filled thin four-sided shapes are lines drawn as outlines (Chrome, some CAD PDF printers): keep their centre line.
    if (!stroked && segs.length < maxSegs) for (const g of groups) { const c = thinCentre(g); if (c) segs.push(c); }
    if (sub && sub.n >= 2) curves.push(sub);
    for (const c of curves) {
      const w = c.maxX - c.minX, h = c.maxY - c.minY;
      if (w > 2 && h > 2 && w < 120 && Math.abs(w - h) <= 0.15 * Math.max(w, h)) circles.push({ cx: (c.minX + c.maxX) / 2, cy: (c.minY + c.maxY) / 2, r: (w + h) / 4 });
    }
    pend = []; groups = []; curves = []; sub = null;
  };
  for (let i = 0; i < fnArray.length; i++) {
    const f = fnArray[i], a = argsArray[i] as unknown[];
    if (f === O.save) stack.push(ctm);
    else if (f === O.restore) ctm = stack.pop() ?? ctm;
    else if (f === O.transform) ctm = mul(ctm, a as unknown as M);
    else if (f === O.paintFormXObjectBegin) { stack.push(ctm); const m = a?.[0] as number[] | null; if (m && m.length === 6) ctm = mul(ctm, m as M); }
    else if (f === O.paintFormXObjectEnd) ctm = stack.pop() ?? ctm;
    else if (f === O.constructPath) {
      const ops = a[0] as number[], c = a[1] as number[]; let k = 0;
      for (const op of ops) {
        if (op === O.moveTo) { cx = c[k++]; cy = c[k++]; sx = cx; sy = cy; startSub(cx, cy); }
        else if (op === O.lineTo) { const x = c[k++], y = c[k++]; const [X1, Y1] = T(cx, cy), [X2, Y2] = T(x, y); pend.push({ x1: X1, y1: Y1, x2: X2, y2: Y2 }); grow(x, y); cx = x; cy = y; }
        else if (op === O.curveTo) { grow(c[k], c[k + 1]); grow(c[k + 2], c[k + 3]); k += 4; cx = c[k++]; cy = c[k++]; grow(cx, cy); bump(); }
        else if (op === O.curveTo2 || op === O.curveTo3) { grow(c[k], c[k + 1]); k += 2; cx = c[k++]; cy = c[k++]; grow(cx, cy); bump(); }
        else if (op === O.closePath) { if (cx !== sx || cy !== sy) { const [X1, Y1] = T(cx, cy), [X2, Y2] = T(sx, sy); pend.push({ x1: X1, y1: Y1, x2: X2, y2: Y2 }); } cx = sx; cy = sy; }
        else if (op === O.rectangle) {
          const x = c[k++], y = c[k++], w = c[k++], h = c[k++];
          const p = [[x, y], [x + w, y], [x + w, y + h], [x, y + h]].map(([u, v]) => T(u, v));
          for (let q = 0; q < 4; q++) pend.push({ x1: p[q][0], y1: p[q][1], x2: p[(q + 1) % 4][0], y2: p[(q + 1) % 4][1] });
          cx = x; cy = y; sx = x; sy = y;
        }
      }
    }
    else if (f === O.stroke || f === O.closeStroke || f === O.fillStroke || f === O.eoFillStroke || f === O.closeFillStroke || f === O.closeEOFillStroke) flush(true);
    else if (f === O.fill || f === O.eoFill || f === O.endPath) flush(false);
  }
  return { segs, circles };
}

/** A closed 4-sided outline at most 3 pt thick and at least 3× longer than thick → its centre line; anything else → null. */
function thinCentre(g: Seg[]): Seg | null {
  const pts = g.map((s) => [s.x1, s.y1] as [number, number]);
  if (g.length === 3) pts.push([g[2].x2, g[2].y2]);                 // fill closes the shape by itself
  if (pts.length !== 4 || (g.length === 4 && Math.hypot(g[3].x2 - g[0].x1, g[3].y2 - g[0].y1) > 0.01)) return null;
  const d = (a: number[], b: number[]) => Math.hypot(a[0] - b[0], a[1] - b[1]);
  const s01 = d(pts[0], pts[1]), s12 = d(pts[1], pts[2]);
  const [thick, long] = s01 < s12 ? [s01, s12] : [s12, s01];
  if (thick > 3 || long < thick * 3) return null;
  const mid = (a: number[], b: number[]) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const [p, q] = s01 < s12 ? [mid(pts[0], pts[1]), mid(pts[2], pts[3])] : [mid(pts[1], pts[2]), mid(pts[3], pts[0])];
  return { x1: p[0], y1: p[1], x2: q[0], y2: q[1] };
}

// ---- 2. detection --------------------------------------------------------------------------------------------------
/** Grid labels: A, AA, 1, 12, 12A, A1, SP1A, CEN… (inside a bubble anything up to 5 characters counts). */
const LABEL_FREE = /^[A-Z]{1,2}$|^[A-Z]{0,3}\d{1,3}[A-Z]?$|^\d{1,3}[A-Z]?'?$/;
const cleanLabel = (s: string) => s.replace(/\s+/g, "").toUpperCase();

interface Anchor { cx: number; cy: number; r: number; label: string | null }
interface Run { t0: number; t1: number; ux: number; uy: number; ox: number; oy: number }

function anchorsOf(g: PageGeometry): Anchor[] {
  const centre = (t: TextItem) => ({ x: t.x + (Math.cos(t.rot) * t.w) / 2 - (Math.sin(t.rot) * -t.h * 0.35), y: t.y + (Math.sin(t.rot) * t.w) / 2 - Math.cos(t.rot) * t.h * 0.35 });
  const texts = g.texts.map((t) => ({ t, c: centre(t), s: cleanLabel(t.s) })).filter((x) => x.s.length > 0 && x.s.length <= 5);
  const used = new Set<number>();
  const out: Anchor[] = [];
  for (const c of g.circles) {
    if (c.r < 3 || c.r > 40) continue;
    let best = -1, bd = Infinity;
    texts.forEach((x, k) => { const d = Math.hypot(x.c.x - c.cx, x.c.y - c.cy); if (d < c.r * 0.8 && d < bd && x.t.h <= c.r * 2.2) { bd = d; best = k; } });
    if (best >= 0) used.add(best);
    out.push({ cx: c.cx, cy: c.cy, r: c.r, label: best >= 0 ? texts[best].s : null });
  }
  // labels drawn without a recognisable circle (some exports draw bubbles as many short lines). When bubbles were found,
  // loose text must be about as big as the bubble labels (small notes such as a lone "S" are not grid labels).
  const bubbleH = out.filter((a) => a.label).map((a) => texts.find((x) => x.s === a.label && Math.hypot(x.c.x - a.cx, x.c.y - a.cy) < a.r)?.t.h ?? 0).filter((h) => h > 0).sort((p, q) => p - q);
  const minH = bubbleH.length ? bubbleH[Math.floor(bubbleH.length / 2)] * 0.8 : 4;
  texts.forEach((x, k) => { if (!used.has(k) && LABEL_FREE.test(x.s) && x.t.h >= minH) out.push({ cx: x.c.x, cy: x.c.y, r: Math.max(x.t.h, x.t.w / 2) * 0.9, label: x.s }); });
  // the same bubble seen twice (circle + loose text) → keep the first
  return out.filter((a, k) => !out.some((b, j) => j < k && Math.hypot(a.cx - b.cx, a.cy - b.cy) < Math.min(a.r, b.r) * 0.5));
}

/** Segments bucketed by direction (0.5°) and offset (2 pt), for fast "everything on this straight line" look-ups. */
class LineIndex {
  private map = new Map<string, number[]>();
  constructor(private segs: Seg[]) {
    segs.forEach((s, k) => {
      const len = Math.hypot(s.x2 - s.x1, s.y2 - s.y1); if (len < 0.8) return;
      const [a, o] = this.key(s); const id = `${a}|${o}`;
      let l = this.map.get(id); if (!l) this.map.set(id, (l = [])); l.push(k);
    });
  }
  private key(s: Seg): [number, number] {
    let th = Math.atan2(s.y2 - s.y1, s.x2 - s.x1); if (th < 0) th += Math.PI; if (th >= Math.PI - 1e-9) th -= Math.PI;
    const off = -s.x1 * Math.sin(th) + s.y1 * Math.cos(th);
    return [Math.round((th * 180) / Math.PI / 0.5), Math.round(off / 2)];
  }
  /** segments lying on the line through (ox, oy) with direction (ux, uy), within tol of it. */
  near(ox: number, oy: number, ux: number, uy: number, tol: number): Seg[] {
    let th = Math.atan2(uy, ux); if (th < 0) th += Math.PI; if (th >= Math.PI - 1e-9) th -= Math.PI;
    const off = -ox * Math.sin(th) + oy * Math.cos(th);
    const A = Math.round((th * 180) / Math.PI / 0.5), B = Math.round(off / 2), bins = 360;
    const out: Seg[] = [];
    for (let da = -2; da <= 2; da++) for (let db = -1; db <= 1; db++) {
      let a = A + da, b = B + db;
      if (a < 0) { a += bins; b = -b; } else if (a >= bins) { a -= bins; b = -b; }
      for (const k of this.map.get(`${a}|${b}`) ?? []) {
        const s = this.segs[k];
        const d1 = Math.abs((s.x1 - ox) * -uy + (s.y1 - oy) * ux), d2 = Math.abs((s.x2 - ox) * -uy + (s.y2 - oy) * ux);
        if (d1 <= tol && d2 <= tol) out.push(s);
      }
    }
    return out;
  }
}

/** Longest straight run leaving the bubble along (ux, uy): pieces joined while gaps ≤ joinGap. */
function runFrom(idx: LineIndex, a: Anchor, ux: number, uy: number, ox: number, oy: number, joinGap: number): Run | null {
  const tol = 0.9;
  const pieces = idx.near(ox, oy, ux, uy, tol)
    .map((s) => { const t1 = (s.x1 - a.cx) * ux + (s.y1 - a.cy) * uy, t2 = (s.x2 - a.cx) * ux + (s.y2 - a.cy) * uy; return [Math.min(t1, t2), Math.max(t1, t2)] as [number, number]; })
    .filter(([, t1]) => t1 > a.r * 0.5)
    .sort((p, q) => p[0] - q[0]);
  let end = a.r, start = Infinity;
  for (const [t0, t1] of pieces) {
    if (t0 > end + joinGap) break;
    if (t1 > end) { if (start === Infinity) start = Math.max(a.r, t0); end = t1; }
  }
  return start === Infinity ? null : { t0: start, t1: end, ux, uy, ox, oy };
}

export function detectGrid(g: PageGeometry, opt: DetectOptions = DETECT_DEFAULTS): GridLine[] {
  const idx = new LineIndex(g.segs);
  const minLen = opt.minLength * Math.min(g.width, g.height);
  const found: { a: Anchor; run: Run }[] = [];
  // a quick neighbourhood index for the starter pieces around each bubble
  const cell = 40, near = new Map<string, number[]>();
  g.segs.forEach((s, k) => {
    for (const [x, y] of [[s.x1, s.y1], [s.x2, s.y2]]) { const id = `${Math.floor(x / cell)}|${Math.floor(y / cell)}`; let l = near.get(id); if (!l) near.set(id, (l = [])); l.push(k); }
  });
  for (const a of anchorsOf(g)) {
    const reach = a.r * 1.8 + 3, seen = new Set<number>();
    let best: Run | null = null;
    for (let gx = Math.floor((a.cx - reach) / cell); gx <= Math.floor((a.cx + reach) / cell); gx++)
      for (let gy = Math.floor((a.cy - reach) / cell); gy <= Math.floor((a.cy + reach) / cell); gy++)
        for (const k of near.get(`${gx}|${gy}`) ?? []) {
          if (seen.has(k)) continue; seen.add(k);
          const s = g.segs[k];
          const len = Math.hypot(s.x2 - s.x1, s.y2 - s.y1); if (len < 1.5) continue;
          const d1 = Math.hypot(s.x1 - a.cx, s.y1 - a.cy), d2 = Math.hypot(s.x2 - a.cx, s.y2 - a.cy);
          const [nx, ny, fx, fy] = d1 <= d2 ? [s.x1, s.y1, s.x2, s.y2] : [s.x2, s.y2, s.x1, s.y1];
          if (Math.min(d1, d2) > reach || Math.min(d1, d2) < a.r * 0.6) continue;          // starts at the bubble's edge
          const ux = (fx - nx) / len, uy = (fy - ny) / len;
          if ((nx - a.cx) * ux + (ny - a.cy) * uy <= 0) continue;                           // pointing away from the bubble
          if (Math.abs((a.cx - nx) * -uy + (a.cy - ny) * ux) > a.r * 0.35 + 1) continue;    // its line passes through the centre
          const run = runFrom(idx, a, ux, uy, nx, ny, opt.joinGap);
          if (run && (!best || run.t1 - run.t0 > best.t1 - best.t0)) best = run;
        }
    if (best && best.t1 - best.t0 >= minLen) found.push({ a, run: best });
  }
  // one gridline with a bubble at each end → one line (the readable label wins; two different labels → check)
  const lines: (GridLine & { _ux: number; _uy: number; _off: number })[] = [];
  for (const { a, run } of found.sort((p, q) => (q.run.t1 - q.run.t0) - (p.run.t1 - p.run.t0))) {
    const x1 = a.cx + run.ux * run.t0, y1 = a.cy + run.uy * run.t0, x2 = a.cx + run.ux * run.t1, y2 = a.cy + run.uy * run.t1;
    const off = -x1 * run.uy + y1 * run.ux;
    const same = lines.find((l) => Math.abs(Math.abs(l._ux * run.ux + l._uy * run.uy) - 1) < 2e-4 && Math.abs(Math.abs(l._off) - Math.abs(off)) < 2
      && Math.abs((x1 - l.x1 * g.width) * -l._uy + (y1 - l.y1 * g.height) * l._ux) < 2);
    if (same) {
      const pts = [[same.x1 * g.width, same.y1 * g.height], [same.x2 * g.width, same.y2 * g.height], [x1, y1], [x2, y2]];
      const t = pts.map(([x, y]) => x * same._ux + y * same._uy); const lo = t.indexOf(Math.min(...t)), hi = t.indexOf(Math.max(...t));
      Object.assign(same, { x1: pts[lo][0] / g.width, y1: pts[lo][1] / g.height, x2: pts[hi][0] / g.width, y2: pts[hi][1] / g.height });
      if (!same.label) same.label = a.label; else if (a.label && a.label !== same.label) same.check = true;
      continue;
    }
    lines.push({ id: `d${lines.length + 1}`, x1: x1 / g.width, y1: y1 / g.height, x2: x2 / g.width, y2: y2 / g.height, label: a.label, zone: null, _ux: run.ux, _uy: run.uy, _off: off });
  }
  const byLabel = new Map<string, number>();
  for (const l of lines) if (l.label) byLabel.set(l.label, (byLabel.get(l.label) ?? 0) + 1);
  return lines.map(({ _ux, _uy, _off, ...l }) => (l.label && (byLabel.get(l.label) ?? 0) > 1 ? { ...l, check: true } : l))
    .map((l) => ({ ...l, x1: round5(l.x1), y1: round5(l.y1), x2: round5(l.x2), y2: round5(l.y2) }));
}
const round5 = (v: number) => Math.round(v * 1e5) / 1e5;

// ---- 3. counts, angles, zones --------------------------------------------------------------------------------------
/** A line is amber ("to check") without a label or when flagged — same rule as issues_grid_save (0016). */
export const isAmber = (l: GridLine) => !l.label?.trim() || !!l.check;
export function gridCounts(lines: GridLine[]): { labelled: number; toCheck: number } {
  const toCheck = lines.filter(isAmber).length;
  return { labelled: lines.length - toCheck, toCheck };
}

/** Angle of a line on the real page, degrees, -90 < a ≤ 90 (0 = horizontal; positive = clockwise on screen). */
export function lineAngle(l: Pick<GridLine, "x1" | "y1" | "x2" | "y2">, ratio: number): number {
  let a = (Math.atan2((l.y2 - l.y1) * ratio, l.x2 - l.x1) * 180) / Math.PI;
  if (a <= -90) a += 180; if (a > 90) a -= 180;
  return Math.round(a * 10) / 10;
}

/** Turn a line about its middle to `deg` (A6.4 Selected line › Angle). */
export function setAngle<L extends GridLine>(l: L, deg: number, ratio: number): L {
  const mx = (l.x1 + l.x2) / 2, my = ((l.y1 + l.y2) / 2) * ratio, half = Math.hypot(l.x2 - l.x1, (l.y2 - l.y1) * ratio) / 2;
  const r = (deg * Math.PI) / 180, dx = Math.cos(r) * half, dy = Math.sin(r) * half;
  return { ...l, x1: round5(mx - dx), y1: round5((my - dy) / ratio), x2: round5(mx + dx), y2: round5((my + dy) / ratio) };
}

/** Grid angle of a zone (A6.5 "grid angle -28°"): the angle of its most horizontal family. */
export function gridAngle(lines: GridLine[], ratio: number): number {
  const fams = families(lines, ratio);
  if (!fams.length) return 0;
  const a = fams.map((f) => f.angle).sort((p, q) => Math.abs(p) - Math.abs(q))[0];
  return Math.round(a);
}

export function pointInPolygon(x: number, y: number, pts: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i], [xj, yj] = pts[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
export const zoneAt = (zones: GridZone[], x: number, y: number) => zones.find((z) => pointInPolygon(x, y, z.points)) ?? null;
/** Share (0–1) of a line that runs inside a polygon — 21 points along it. */
export function shareInside(l: GridLine, pts: [number, number][]): number {
  let n = 0;
  for (let k = 0; k <= 20; k++) if (pointInPolygon(l.x1 + ((l.x2 - l.x1) * k) / 20, l.y1 + ((l.y2 - l.y1) * k) / 20, pts)) n++;
  return n / 21;
}
/** A6.5: a new zone takes the lines running mostly (60 %+) inside it (v0.7.1, v070-031 — before, only lines whose middle was inside,
 *  so a zone drawn over part of a long line got none and the cursor read nothing). */
export const linesInZone = (lines: GridLine[], z: GridZone) => lines.filter((l) => shareInside(l, z.points) >= 0.6);

// ---- 4. pin reading ------------------------------------------------------------------------------------------------
interface Family { angle: number; nx: number; ny: number; lines: { label: string; off: number }[] }
const natural = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

/** Labelled lines split into (up to) two families by direction; each sorted across the grid. */
function families(lines: GridLine[], ratio: number): Family[] {
  const fams: Family[] = [];
  for (const l of lines) {
    if (!l.label?.trim()) continue;
    const ang = lineAngle(l, ratio);
    let f = fams.find((x) => { const d = Math.abs(x.angle - ang) % 180; return Math.min(d, 180 - d) < 25; });
    if (!f) { const r = (ang * Math.PI) / 180; f = { angle: ang, nx: -Math.sin(r), ny: Math.cos(r), lines: [] }; fams.push(f); }
    f.lines.push({ label: l.label.trim(), off: l.x1 * f.nx + l.y1 * ratio * f.ny });
  }
  for (const f of fams) f.lines.sort((a, b) => a.off - b.off);
  // letters first ("C/17"), then the other family; same kind → the more horizontal family first
  const lettery = (f: Family) => f.lines.filter((l) => /^[A-Z]+$/i.test(l.label)).length / f.lines.length;
  return fams.sort((a, b) => (lettery(b) - lettery(a)) || (Math.abs(a.angle) - Math.abs(b.angle))).slice(0, 2);
}

/** Where a point sits in one family: "C" (on the line) or "16–20" (between two). */
function readFamily(f: Family, x: number, y: number, ratio: number): string | null {
  if (!f.lines.length) return null;
  const o = x * f.nx + y * ratio * f.ny, L = f.lines;
  const spacing = L.length > 1 ? (L[L.length - 1].off - L[0].off) / (L.length - 1) : 0.05;
  const onTol = Math.min(0.25 * spacing, 0.012);
  let k = 0; while (k < L.length - 1 && L[k + 1].off <= o) k++;
  const nearest = L.reduce((b, l) => (Math.abs(l.off - o) < Math.abs(b.off - o) ? l : b));
  if (Math.abs(nearest.off - o) <= onTol || o <= L[0].off || o >= L[L.length - 1].off) return nearest.label;
  const pair = [L[k].label, L[k + 1].label].sort(natural);
  return `${pair[0]}–${pair[1]}`;
}

/** A5.2 / A6.4 reading of a point (0–1 across / down the page): "C/17", "D/12–13", "TB · C/4–5"; null = no grid. */
export function pinReading(lines: GridLine[], zones: GridZone[], x: number, y: number, ratio: number): string | null {
  const read = (own: GridLine[]) => families(own, ratio).map((f) => readFamily(f, x, y, ratio)).filter(Boolean) as string[];
  const main = () => read(lines.filter((l) => !l.zone || !zones.some((z) => z.id === l.zone)));
  const zone = zoneAt(zones, x, y);
  if (zone) {
    const parts = read(lines.filter((l) => l.zone === zone.id));
    if (parts.length) return `${zone.short} · ${parts.join("/")}`;
    // v0.7.1 (v070-031): a zone with no labelled lines of its own reads the main grid instead of nothing
  }
  const parts = main();
  return parts.length ? parts.join("/") : null;
}

/** Copy lines (and zones) from another component: fresh ids so the two grids never share one. */
export function copyGrid(lines: GridLine[], zones: GridZone[]): { lines: GridLine[]; zones: GridZone[] } {
  const zid = new Map(zones.map((z, k) => [z.id, `z${Date.now().toString(36)}${k}`]));
  return { zones: zones.map((z) => ({ ...z, id: zid.get(z.id)!, points: z.points.map((p) => [...p] as [number, number]) })),
    lines: lines.map((l, k) => ({ ...l, id: `c${k + 1}`, zone: l.zone ? zid.get(l.zone) ?? null : null })) };
}
